/**
 * JWT Token utilities for client-side token handling
 */

// Base JWT payload for all user types
export interface BaseJWTPayload {
  user_type: 'restaurant' | 'customer' | 'vh_staff';
  email: string;
  first_name?: string;
  last_name?: string;
  picture_url?: string;
  exp: number;
  iat: number;
  jti: string;
}

// VH Staff user JWT payload (operational access only, no account settings)
export interface VHStaffJWTPayload extends BaseJWTPayload {
  user_type: 'vh_staff';
  staff_id: number;
  name?: string;
  role: 'vh_staff';
  access_level: 'operational';
  restaurants: Array<{ rest_id: number; rest_account_id: number; name: string }>;
  active_restaurant_id: number;
  active_rest_account_id: number;
  rest_id: number;
  rest_account_id: number;
  permissions: string[];
  remember_me?: boolean;
}

// Restaurant user JWT payload (with subscriptions)
export interface RestaurantJWTPayload extends BaseJWTPayload {
  user_type: 'restaurant';
  user_id: number;
  role?: string;
  rest_id?: number;
  restaurant_name?: string;
  access_level?: string;
  ownership_scope?: string;
  subscription?: {
    plan_name?: string;
    subscription_status?: string;
    is_trial?: boolean;
    trial_end?: string;
  };
  permissions?: string[];
  owned_restaurants?: number[];
  subscription_required?: boolean;
  needs_onboarding?: boolean;
}

// Customer user JWT payload (no subscriptions)
export interface CustomerJWTPayload extends BaseJWTPayload {
  user_type: 'customer';
  cust_id: number;
  is_verified?: boolean;
  // Customers don't have subscriptions, restaurants, or complex permissions
}

export type JWTPayload = RestaurantJWTPayload | CustomerJWTPayload | VHStaffJWTPayload;

/**
 * Decode JWT token (client-side only - for reading claims)
 * WARNING: This does not verify the token signature!
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(decoded);
    
    return parsed as JWTPayload;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded) return true;
  
  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
};

/**
 * Get user subscription status from JWT (only for restaurant users)
 */
export const getUserSubscriptionStatus = (token: string) => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;

  // Customers don't have subscriptions
  if (decoded.user_type === 'customer') {
    return {
      hasActiveSubscription: false,
      isTrialActive: false,
      subscriptionRequired: false,
      needsOnboarding: false,
      planName: null,
      trialEnd: null,
      hasRestaurant: false,
      restaurantName: null,
      isCustomer: true
    };
  }

  // Restaurant users have subscription information
  const restaurantPayload = decoded as RestaurantJWTPayload;
  return {
    hasActiveSubscription: restaurantPayload.subscription?.status === 'active' || restaurantPayload.subscription?.subscription_status === 'active',
    isTrialActive: restaurantPayload.subscription?.is_trial === true,
    subscriptionRequired: restaurantPayload.subscription_required === true,
    needsOnboarding: restaurantPayload.needs_onboarding === true,
    planName: restaurantPayload.subscription?.plan || restaurantPayload.subscription?.plan_name,
    trialEnd: restaurantPayload.subscription?.trial_end || restaurantPayload.subscription?.trial_expires,
    hasRestaurant: !!restaurantPayload.rest_id,
    restaurantName: restaurantPayload.restaurant_name,
    isCustomer: false
  };
};

/**
 * Get user display information from JWT
 */
export const getUserDisplayInfo = (token: string) => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;

  // Get user ID based on user type
  let userId: number | undefined;
  if (decoded.user_type === 'customer') {
    userId = (decoded as CustomerJWTPayload).cust_id;
  } else if (decoded.user_type === 'vh_staff') {
    userId = (decoded as VHStaffJWTPayload).staff_id;
  } else {
    userId = (decoded as RestaurantJWTPayload).user_id;
  }

  // Get role based on user type
  let role: string | null = null;
  if (decoded.user_type === 'restaurant') {
    role = (decoded as RestaurantJWTPayload).role || null;
  } else if (decoded.user_type === 'vh_staff') {
    role = 'vh_staff';
  }

  // Get name for VH staff (uses 'name' instead of first_name/last_name)
  const staffName = decoded.user_type === 'vh_staff' ? (decoded as VHStaffJWTPayload).name : null;
  const displayName = staffName || `${decoded.first_name || ''} ${decoded.last_name || ''}`.trim();

  return {
    userId,
    email: decoded.email,
    firstName: decoded.first_name || staffName?.split(' ')[0],
    lastName: decoded.last_name || staffName?.split(' ').slice(1).join(' '),
    fullName: displayName,
    pictureUrl: decoded.picture_url,
    role,
    userType: decoded.user_type,
    isStaff: decoded.user_type === 'vh_staff',
    isVerified: decoded.user_type === 'customer' ? (decoded as CustomerJWTPayload).is_verified : undefined
  };
};

/**
 * Check if user is VH Staff (operational access only)
 */
export const isVHStaff = (token: string): boolean => {
  const decoded = decodeJWT(token);
  return decoded?.user_type === 'vh_staff';
};